import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftIcon,
  TickCircleIcon,
  TimeIcon,
  WarningSignIcon,
} from "evergreen-ui";
import { formatInvoiceCurrencySummary } from "../financialSummaries";
import { importedPaymentReviewPeriod, isImportedPaymentPending } from "../importedPaymentReview";
import { formatDocumentDate, invoiceNumber } from "../invoiceUtils";
import { sortInvoices } from "../invoiceList";
import { usePrivacy } from "../privacy/PrivacyContext";
import { useUiLanguage } from "../uiLanguage";
import { ViewHeading } from "../components/SharedUI";

const periodLabel = (period, language) => {
  if (!period) return "Latest imported period";
  const date = new Date(`${period}-01T12:00:00`);
  return Number.isNaN(date.getTime())
    ? period
    : new Intl.DateTimeFormat(language === "sq" ? "sq-AL" : "en", { month: "long", year: "numeric" }).format(date);
};

const ImportedPaymentReviewView = ({ invoices = [], close, reviewPayment }) => {
  const privacy = usePrivacy();
  const { language, t } = useUiLanguage();
  const pending = useMemo(() => sortInvoices(invoices.filter(isImportedPaymentPending)), [invoices]);
  const latestPeriod = useMemo(() => importedPaymentReviewPeriod(pending), [pending]);
  const [scope, setScope] = useState("latest");
  const visible = scope === "all"
    ? pending
    : pending.filter((invoice) => String(invoice.issuedDate || invoice.issued || "").startsWith(latestPeriod));

  useEffect(() => {
    if (scope === "latest" && !visible.length && pending.length) setScope("all");
  }, [pending.length, scope, visible.length]);

  const confirmVisible = () => reviewPayment(visible.map((invoice) => invoice.id), "paid");

  return <div className="is2-view is2-payment-review" data-cy="imported-payment-review">
    <ViewHeading
      eyebrow="Imported history"
      title="Review payment assumptions"
      description="Historical invoices are provisionally counted as paid. Confirm them, or mark exceptions as still unpaid."
      actions={<button className="is2-button is2-button--quiet" onClick={close}><ArrowLeftIcon size={15} /> {t("Back to invoices")}</button>}
    />

    <section className="is2-payment-review__summary">
      <span className="is2-payment-review__summary-icon"><WarningSignIcon size={19} /></span>
      <span><strong>{privacy.number(pending.length)} {t("imported payment states still need confirmation")}</strong><small>{t("They are treated as paid until you identify an unpaid invoice.")}</small></span>
      <span><small>{t("Assumed paid value")}</small><strong>{privacy.amount(formatInvoiceCurrencySummary(pending))}</strong></span>
    </section>

    <div className="is2-payment-review__toolbar">
      <div className="is2-segmented" role="tablist" aria-label="Imported payment review period">
        <button className={scope === "latest" ? "is-active" : ""} onClick={() => setScope("latest")} role="tab" aria-selected={scope === "latest"}>{periodLabel(latestPeriod, language)}</button>
        <button className={scope === "all" ? "is-active" : ""} onClick={() => setScope("all")} role="tab" aria-selected={scope === "all"}>{t("All unconfirmed")} <span>{privacy.number(pending.length)}</span></button>
      </div>
      {visible.length ? <button className="is2-button is2-button--primary" onClick={confirmVisible}><TickCircleIcon size={15} /> {t("Confirm visible as paid")}</button> : null}
    </div>

    <section className="is2-section is2-payment-review__list">
      {visible.map((invoice) => <article key={invoice.id} className="is2-payment-review__row">
        <span className="is2-payment-review__state"><TimeIcon size={15} /></span>
        <span><strong>{privacy.invoice(invoiceNumber(invoice))}</strong><small>{privacy.client(invoice.client)} · {t("issued")} {privacy.date(formatDocumentDate(invoice.issuedDate, invoice.issued))}</small></span>
        <strong>{privacy.amount(formatInvoiceCurrencySummary([invoice]))}</strong>
        <span className="is2-payment-review__actions">
          <button className="is2-button is2-button--quiet" onClick={() => reviewPayment(invoice.id, "open")}>{t("Not paid")}</button>
          <button className="is2-button is2-button--primary" onClick={() => reviewPayment(invoice.id, "paid")}><TickCircleIcon size={14} /> {t("Confirm paid")}</button>
        </span>
      </article>)}
      {!pending.length ? <div className="is2-empty"><TickCircleIcon size={24} /><strong>{t("Imported payments are reviewed")}</strong><span>{t("There are no unresolved historical payment assumptions.")}</span><button className="is2-button is2-button--quiet" onClick={close}>{t("Return to invoices")}</button></div> : null}
      {pending.length && !visible.length ? <div className="is2-empty"><TimeIcon size={24} /><strong>{t("No unconfirmed invoices in this period")}</strong><span>{t("Open all unconfirmed payments to continue.")}</span></div> : null}
    </section>
  </div>;
};

export default ImportedPaymentReviewView;
